import { Component, ViewEncapsulation } from '@angular/core';
import {
	AbstractControl,
	FormControl,
	FormGroup,
	ValidatorFn,
	Validators,
	FormsModule,
	ReactiveFormsModule
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { MatRippleModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { NgIf, NgClass } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { HttpErrorResponse } from '@angular/common/http';

import { pageTransitions } from '../../../../shared/animations/page-transitions';
import { User } from '../../../../shared/models/user';
import { UserInfo } from '../../../../shared/models/user-info';
import { TranslationService } from '../../../../shared/services/translation.service';
import { UserService } from '../../../../shared/services/user.service';
import { UserCardService } from '../../../services/user-card.service';
import { UserBasics } from '../../../../shared/models/user-basics';
import { LoadingSpinnerComponent } from '../../../../shared/components/utils/loading-spinner.component';

const passwordPatternLowerCase = /[a-z]/;
const passwordPatternUpperCase = /[A-Z]/;
const passwordPatternDigit = /\d/;
const passwordPatternSpecialChars = /[!"#$%&'()*+,./:;<=>?@[\\\]^_`{|}~-]/;
const passwordMinChars = 15;

function namedValidator(validator: ValidatorFn, name: string): ValidatorFn {
	return control => {
		const errors = validator(control);
		if (!errors) {
			return null;
		}
		return Object.fromEntries(Object.entries(errors).map(([key, value]) => [key + name, value]));
	};
}

@Component({
	selector: 'copo-user-self-card-security',
	templateUrl: './user-self-card-security.component.html',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	styleUrl: './user-self-card-security.component.css',
	imports: [
		TranslateModule,
		FormsModule,
		ReactiveFormsModule,
		NgIf,

		MatFormFieldModule,

		MatInputModule,
		MatIconModule,

		MatButtonModule,
		MatRippleModule,
		NgClass,
		LoadingSpinnerComponent
	]
})
export class UserCardSecurityComponent {
	passwordPatternLowerCase = namedValidator(Validators.pattern(passwordPatternLowerCase), 'LowerCase');
	passwordPatternUpperCase = namedValidator(Validators.pattern(passwordPatternUpperCase), 'UpperCase');
	passwordPatternDigit = namedValidator(Validators.pattern(passwordPatternDigit), 'Digit');
	passwordPatternSpecialChars = namedValidator(Validators.pattern(passwordPatternSpecialChars), 'SpecialChars');
	passwordMinChars = Validators.minLength(passwordMinChars);
	passwordPatternStr = `(?=.*${ passwordPatternLowerCase.source
	})(?=.*${ passwordPatternUpperCase.source
	})(?=.*${ passwordPatternDigit.source
	})(?=.*${ passwordPatternSpecialChars.source
	}).{${ passwordMinChars
	},}`;

	changePasswordFormGroup: FormGroup;
	updatePasswordSubscription?: Subscription;
	user: User;
	userInfo?: UserInfo;
	loading = false;
	userClone?: UserInfo;
	userBasics?: UserBasics;
	userForm!: FormGroup;
	userId?: number;
	enableChangeButton = false;
	errorMessage = '';
	passwordError = false;
	showPassword: Record<string, boolean> = {
		currentPassword: false,
		newPassword: false,
		confirmPassword: false,
	};

	constructor(
		private readonly userCardService: UserCardService,

		private readonly snackBar: MatSnackBar,
		private readonly translationService: TranslationService,
		private readonly userService: UserService,
		private readonly route: ActivatedRoute,
		private readonly router: Router
	) {
		this.user = this.userService.user;
		const res = <{ user: UserInfo }> this.route.parent?.snapshot.data;
		this.userInfo = res['user'];
		this.changePasswordFormGroup = new FormGroup(
			{
				currentPwd: new FormControl('', [
					Validators.required,
					this.passwordPatternLowerCase,
					this.passwordPatternUpperCase,
					this.passwordPatternDigit,
					this.passwordPatternSpecialChars,
					this.passwordMinChars
				]),
				newPwd: new FormControl('', [
					Validators.required,
					this.passwordPatternLowerCase,
					this.passwordPatternUpperCase,
					this.passwordPatternDigit,
					this.passwordPatternSpecialChars,
					this.passwordMinChars
				]),
				confirmNewPwd: new FormControl('', [Validators.required]),
			},
			this.passwordMatchingValidatior
		);
		this.userForm = new FormGroup({
			userName: new FormControl('', [Validators.required])
		});
		this.updateUserForm(this.userInfo);
	}

	get newPwd() {
		return this.changePasswordFormGroup.get('newPwd');
	}

	get currentPwd() {
		return this.changePasswordFormGroup.get('currentPwd');
	}

	get confirmNewPwd() {
		return this.changePasswordFormGroup.get('confirmNewPwd');
	}

	async reload() {
		const currentUrl = this.router.url;
		await this.router.navigate(['redirect'], { queryParams: { path: currentUrl }, replaceUrl: true });
	}

	passwordMatchingValidatior = (form: AbstractControl) => {
		const password = form.get('newPwd');
		const confirmation = form.get('confirmNewPwd');

		if (!password || !confirmation) {
			return null;
		}

		if ((<string>confirmation.value).length > 0 && confirmation.value !== password.value) {
			confirmation.setErrors({ notMatch: true });
		}

		return null;
	};

	updatePassword() {
		this.showPassword['currentPassword'] = false;
		this.showPassword['newPassword'] = false;
		this.showPassword['confirmPassword'] = false;
		if (!this.changePasswordFormGroup.invalid) {
			this.loading = true;
			this.updatePasswordSubscription = this.userCardService.updateUserPassword(
				<string> this.changePasswordFormGroup.get('currentPwd')?.value,
				<string> this.changePasswordFormGroup.get('newPwd')?.value,
				<string> this.changePasswordFormGroup.get('confirmNewPwd')?.value
			).subscribe({
				next: async () => {
					this.snackBar.open(
						this.translationService.getTR(
							'settings.password-updated-successfully'
						),
						undefined,
						{
							duration: 5000,
						}
					);
					await this.reload();
					this.loading = false;
				},
				error: (error: unknown) => {
					console.error(error);
					const httpErrorResponse = <HttpErrorResponse>error;
					if (typeof httpErrorResponse.error === 'object' && httpErrorResponse.error !== null
						&& (<{ message: string }>httpErrorResponse.error).message && httpErrorResponse.status === 403) {
						this.passwordError = true;
						this.errorMessage = (<{ code: string }>httpErrorResponse.error).code;
						this.loading = false;
					}
				},
			});
		}
	}

	match(type: string) {
		switch (type) {
		case 'charLength':
			return (<string> this.changePasswordFormGroup.get('newPwd')?.value).length >= 15;
		case 'lower':
			return passwordPatternLowerCase.test(<string> this.changePasswordFormGroup.get('newPwd')?.value);
		case 'upper':
			return passwordPatternUpperCase.test(<string> this.changePasswordFormGroup.get('newPwd')?.value);
		case 'number':
			return passwordPatternDigit.test(<string> this.changePasswordFormGroup.get('newPwd')?.value);
		case 'special':
			return passwordPatternSpecialChars.test(<string> this.changePasswordFormGroup.get('newPwd')?.value);
		default:
			return undefined;
		}
	}

	onEdit() {
		if (this.userInfo) {
			this.userClone = {
				...this.userInfo,
				address: {
					...this.userInfo.address
				}
			};
		}
		this.enableChangeButton = true;
	}

	updateUserForm(user?: UserInfo) {
		if (!user) {
			return;
		}
		this.userForm.patchValue({
			userName: user.username
		});
	}

	updateUserInfo() {
		if (!this.userInfo) {
			return;
		}
		this.userInfo.username = <string> this.userForm.get('userName')?.value;
	}

	handlePasswordVisibility(field: string): void {
		this.showPassword[field] = !this.showPassword[field];
	}

	async onSave() {
		this.updateUserInfo();
		if (this.userInfo === undefined) {
			return;
		}
		this.userBasics = {
			login: this.userClone && this.userInfo.username !== this.userClone.username
				? this.userInfo.username : undefined,
		};
		const userIdToUpdate = this.userId ? Number(this.userId) : this.user.id;
		try {
			await this.userService.userToUpdate(userIdToUpdate, this.userBasics).then(
				() => {
					if (this.userBasics) {
						if (this.userBasics.firstname) {
							this.userCardService.setFirstName(this.userBasics.firstname);
						}
						if (this.userBasics.lastname) {
							this.userCardService.setLastName(this.userBasics.lastname);
						}
					}
				}
			);
			this.enableChangeButton = false;
		} catch (err) {
			console.error('Error from http put current user detail', err);
			this.snackBar.open(
				this.translationService.getTR(
					'settings.user-update-error'
				),
				undefined,
				{
					duration: 5000,
				}
			);
			this.userInfo = this.userClone;
		}
		this.updateUserForm(this.userInfo);
		this.userClone = undefined;
	}
}
